import { useState, useEffect } from "react";
import { API, AUTH, PASS_LABELS, toLocalDateStr, fmtDate, fmtMoney, exportXLSX } from "../../adminUtils";
import ExportBar from "./ExportBar";

const STAFF_MAP = {};

export function LegacyAttendanceReport() {
  const today  = toLocalDateStr(new Date());
  const [rangeEnd, setRangeEnd] = useState(today);
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);

  const rangeStart = (() => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() - 13); return toLocalDateStr(d); })();
  const shiftRange = dir => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() + dir * 14); setRangeEnd(toLocalDateStr(d)); };

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/reports/attendance?from=${rangeStart}&to=${rangeEnd}`, { headers: AUTH })
      .then(r => r.json()).then(d => setData(d.classes ?? [])).catch(() => setData([])).finally(() => setLoading(false));
  }, [rangeEnd]);

  const byTeacher = {};
  for (const c of data) {
    const t = c.teacher || "Unknown";
    if (!byTeacher[t]) byTeacher[t] = [];
    byTeacher[t].push(c);
  }

  const doExport = () => {
    const rows = data.map(c => ({ Date: c.date, Class: c.title, Teacher: c.teacher,
      Confirmed: c.confirmed, Cancelled: c.cancelled,
      Pay: (c.confirmed * (STAFF_MAP[c.teacher] ?? 0)).toFixed(2) }));
    exportXLSX("Attendance", rows, `iy-sp-attendance-${rangeStart}-${rangeEnd}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-range">
        <button onClick={() => shiftRange(-1)}>← Prev</button>
        <span className="adm-range-label">{rangeStart} – {rangeEnd}</span>
        <button onClick={() => shiftRange(1)} disabled={rangeEnd >= today}>Next →</button>
      </div>
      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && <ExportBar onXLSX={doExport} disabled={data.length === 0} />}
      {Object.entries(byTeacher).map(([teacher, classes]) => {
        const rate           = STAFF_MAP[teacher] ?? 0;
        const totalConfirmed = classes.reduce((s, c) => s + c.confirmed, 0);
        const totalPay       = classes.reduce((s, c) => s + c.confirmed * rate, 0);
        return (
          <div key={teacher} className="adm-report-teacher">
            <div className="adm-report-teacher-header">
              <span className="adm-report-teacher-name">{teacher}</span>
              <span className="adm-report-teacher-rate">${rate}/student</span>
            </div>
            <table className="adm-report-table">
              <thead><tr><th>Date</th><th>Class</th><th>Confirmed</th><th>Cancelled</th><th>Pay</th></tr></thead>
              <tbody>
                {classes.map((c, i) => (
                  <tr key={i}>
                    <td>{c.date}</td><td>{c.title}</td><td>{c.confirmed}</td>
                    <td className="adm-report-cancelled">{c.cancelled || 0}</td>
                    <td>{fmtMoney(c.confirmed * rate)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="adm-report-subtotal"><td colSpan={2}>Subtotal</td><td>{totalConfirmed}</td><td></td><td>{fmtMoney(totalPay)}</td></tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export function LegacyRevenueReport() {
  const today  = toLocalDateStr(new Date());
  const [rangeEnd, setRangeEnd]         = useState(today);
  const [revenue, setRevenue]           = useState(null);
  const [alerts, setAlerts]             = useState([]);
  const [loading, setLoading]           = useState(false);
  const [reminderState, setReminderState] = useState("idle");

  const rangeStart = (() => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() - 13); return toLocalDateStr(d); })();
  const shiftRange = dir => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() + dir * 14); setRangeEnd(toLocalDateStr(d)); };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/reports/revenue?from=${rangeStart}&to=${rangeEnd}`, { headers: AUTH }).then(r => r.json()),
      fetch(`${API}/reports/pass-alerts`, { headers: AUTH }).then(r => r.json()),
    ]).then(([rev, al]) => { setRevenue(rev); setAlerts(al.alerts ?? []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [rangeEnd]);

  const handleSendReminders = async () => {
    setReminderState("sending");
    try {
      await fetch(`${API}/reports/pass-alerts/remind-all`, {
        method: "POST", headers: AUTH, body: JSON.stringify({ alerts }),
      });
      setReminderState("sent");
    } catch { setReminderState("idle"); }
  };

  const doExport = () => {
    if (!revenue) return;
    const rows = [
      { Type: "Drop-in classes", Count: revenue.dropins.count, Total: revenue.dropins.total },
      ...Object.entries(revenue.passes ?? {}).map(([t, d]) => ({ Type: PASS_LABELS[t] ?? t, Count: d.count, Total: d.total })),
      { Type: "GRAND TOTAL", Total: revenue.grandTotal },
    ];
    exportXLSX("Revenue", rows, `iy-sp-revenue-${rangeStart}-${rangeEnd}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-range">
        <button onClick={() => shiftRange(-1)}>← Prev</button>
        <span className="adm-range-label">{rangeStart} – {rangeEnd}</span>
        <button onClick={() => shiftRange(1)} disabled={rangeEnd >= today}>Next →</button>
      </div>
      {loading && <p className="adm-loading">Loading…</p>}
      {revenue && !loading && (
        <>
          <ExportBar onXLSX={doExport} />
          <table className="adm-report-table">
            <thead><tr><th>Type</th><th>Count</th><th>Total</th></tr></thead>
            <tbody>
              <tr><td>Drop-in classes</td><td>{revenue.dropins.count}</td><td>{fmtMoney(revenue.dropins.total)}</td></tr>
              {Object.entries(revenue.passes ?? {}).map(([t, d]) => (
                <tr key={t}><td>{PASS_LABELS[t] ?? t}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td></tr>
              ))}
            </tbody>
            <tfoot><tr className="adm-report-subtotal"><td colSpan={2}>Total</td><td>{fmtMoney(revenue.grandTotal)}</td></tr></tfoot>
          </table>

          <h4 className="adm-report-section-title" style={{ marginTop: "2rem" }}>Pass Alerts</h4>
          {alerts.length === 0
            ? <p className="adm-empty">No passes expiring soon or running low.</p>
            : (
              <>
                {reminderState === "idle"    && <button className="adm-btn-ghost" onClick={() => setReminderState("confirm")}>Send reminders to {alerts.length} student{alerts.length !== 1 ? "s" : ""}</button>}
                {reminderState === "confirm" && <div className="adm-remind-confirm"><span>Send to {alerts.length} student{alerts.length !== 1 ? "s" : ""}?</span><button className="adm-btn-primary" onClick={handleSendReminders}>Yes, send</button><button className="adm-btn-ghost" onClick={() => setReminderState("idle")}>Cancel</button></div>}
                {reminderState === "sending" && <p className="adm-loading">Sending…</p>}
                {reminderState === "sent"    && <p className="adm-remind-sent">✓ Reminders sent to {alerts.length} student{alerts.length !== 1 ? "s" : ""}</p>}
                <table className="adm-report-table adm-alerts-table">
                  <thead><tr><th>Student</th><th>Pass</th><th>Remaining</th><th>Expires</th><th>Alert</th></tr></thead>
                  <tbody>
                    {alerts.map((a, i) => (
                      <tr key={i}><td>{a.studentEmail}</td><td>{a.passLabel}</td><td>{a.classesRemaining}</td>
                        <td>{a.expiresAt ? fmtDate(a.expiresAt) : "—"}</td><td>{a.reasons.join(" · ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )
          }
        </>
      )}
    </div>
  );
}